/**
 * 🤖 AUTO-GENERATED REACT HOOKS - DO NOT EDIT DIRECTLY!
 *
 * This file is automatically generated from entity configuration files.
 * Any manual changes will be lost when the generator runs again.
 *
 * 🔄 DATA FLOW PIPELINE:
 * 1. Entity Config (config/entities/lookup_release_note_statuses-entity-config.ts) → Single Source of Truth
 * 2. Generated API Client (generated-lookup_release_note_statuses-api-client.ts) → Type-safe API operations
 * 3. THIS FILE → React Query hooks with config-aware field permissions
 * 4. Components → Use these hooks for type-safe data fetching with field restrictions
 *
 * 🛠️ TO MODIFY:
 * - Edit the entity config file: config/entities/lookup_release_note_statuses-entity-config.ts
 * - Run: pnpm run generate:api-clients (regenerates this file)
 *
 * 📋 FIELD PERMISSIONS (from config):
 * - Searchable fields: none
 * - Filterable fields: id, code
 * - Sortable fields: sort_order, created_at, updated_at
 * - Required fields: id, code, name, sort_order, archived, settable_by, visible_to_client, resets_verification, is_reopen, is_terminal, updated_at
 * - Readonly fields: id, created_at, updated_at
 *
 * 🔒 SECURITY: Only fields marked as 'public: true' in the config are accessible
 * 🎯 VALIDATION: Field-level permissions are enforced in hook parameters
 *
 * 🔗 RELATED FILES:
 * - config/entities/lookup_release_note_statuses-entity-config.ts (source configuration)
 * - client/api/generated_clients/generated-lookup_release_note_statuses-api-client.ts (API client)
 * - client/api/generated_schemas/lookup_release_note_statuses.ts (TypeBox schemas and types)
 *
 * Source config: config/entities/lookup_release_note_statuses-entity-config.ts
 * Public fields: 14/14 total fields
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { LookupReleaseNoteStatusesClient, LookupReleaseNoteStatusesQueryOptions } from '../generated_clients/generated-lookup_release_note_statuses-api-client.js';
import {
  LookupReleaseNoteStatusesInsert,
  LookupReleaseNoteStatusesUpdate,
  LookupReleaseNoteStatusesFieldConfig
} from '../../types/generated_schemas/generated-lookup_release_note_statuses-schema.js';

// 🔍 SEARCH/FILTER TYPES MOVED TO BUSINESS LOGIC
//
// Search, filter, and sort types have been moved to business logic hooks
// to maintain clean architectural boundaries.
//
// 📍 For search/filter types, see:
// - client/api/non_generated_business_hooks/lookup_release_note_statuses_business_hooks/

// Query keys with enhanced caching strategy. A list key carries every
// parameter getAll sends, so two different pages never share a cache entry.
export const lookupReleaseNoteStatusesKeys = {
  all: ['lookup_release_note_statuses'] as const,
  lists: () => [...lookupReleaseNoteStatusesKeys.all, 'list'] as const,
  list: (filters: Record<string, any>, page?: Pick<LookupReleaseNoteStatusesQueryOptions, 'limit' | 'offset' | 'sortBy' | 'sortOrder'>) =>
    [...lookupReleaseNoteStatusesKeys.lists(), { filters, ...page }] as const,
  details: () => [...lookupReleaseNoteStatusesKeys.all, 'detail'] as const,
  detail: (id: string) => [...lookupReleaseNoteStatusesKeys.details(), id] as const,
  search: (query: string, fields?: string[]) => [...lookupReleaseNoteStatusesKeys.all, 'search', query, fields] as const,
  filter: (filters: Record<string, any>) => [...lookupReleaseNoteStatusesKeys.all, 'filter', filters] as const,
  sort: (sortBy: string, ascending: boolean) => [...lookupReleaseNoteStatusesKeys.all, 'sort', sortBy, ascending] as const,
  count: (filters?: Record<string, any>) => [...lookupReleaseNoteStatusesKeys.all, 'count', filters] as const,
};

// Client instance hook - uses the shared httpClient
export const useLookupReleaseNoteStatusesClient = () => {
  // Return a new instance of the client that uses the shared httpClient
  return new LookupReleaseNoteStatusesClient();
};

/**
 * Hook to get all lookup_release_note_statuses with optional filtering and sorting
 *
 * @param options - Query options with field-level permission validation
 * @returns React Query result with lookup_release_note_statuses data
 */
export const useLookupReleaseNoteStatusesList = (options?: LookupReleaseNoteStatusesQueryOptions) => {
  const client = useLookupReleaseNoteStatusesClient();
  
  return useQuery({
    queryKey: lookupReleaseNoteStatusesKeys.list(options?.filters || {}, {
      limit: options?.limit,
      offset: options?.offset,
      sortBy: options?.sortBy,
      sortOrder: options?.sortOrder,
    }),
    queryFn: () => client.getAll(options),
  });
};

/**
 * Hook to get a single lookup_release_note_statuse by ID
 *
 * @param id - The lookup_release_note_statuse ID
 * @returns React Query result with single lookup_release_note_statuse data
 */
export const useLookupReleaseNoteStatuses = (id: string) => {
  const client = useLookupReleaseNoteStatusesClient();
  
  return useQuery({
    queryKey: lookupReleaseNoteStatusesKeys.detail(id),
    queryFn: () => client.getById(id),
    enabled: !!id,
  });
};

/**
 * 🔍 SEARCH/FILTER OPERATIONS MOVED TO BUSINESS LOGIC
 *
 * Search and filter operations have been moved to business logic hooks
 * to maintain clean architectural boundaries:
 *
 * - Generated clients = Pure CRUD only (GET, POST, PUT, DELETE, COUNT)
 * - Search/Filter = Complex business logic in /api/business/lookup_release_note_statuses
 *
 * 📍 For search functionality, use:
 * - client/api/non_generated_business_hooks/lookup_release_note_statuses_business_hooks/
 */

/**
 * Hook to create a new lookup_release_note_statuse
 *
 * ⚠️ FIELD RESTRICTIONS: Required fields: id, code, name, sort_order, archived, settable_by, visible_to_client, resets_verification, is_reopen, is_terminal, updated_at
 * ⚠️ READONLY FIELDS: These fields cannot be set: id, created_at, updated_at
 *
 * @returns React Query mutation for creating lookup_release_note_statuse
 */
export const useCreateLookupReleaseNoteStatuses = () => {
  const client = useLookupReleaseNoteStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (data: LookupReleaseNoteStatusesInsert) => client.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupReleaseNoteStatusesKeys.all });
    },
  });
};

/**
 * Hook to update an existing lookup_release_note_statuse
 *
 * ⚠️ READONLY FIELDS: These fields cannot be updated: id, created_at, updated_at
 *
 * @returns React Query mutation for updating lookup_release_note_statuse
 */
export const useUpdateLookupReleaseNoteStatuses = () => {
  const client = useLookupReleaseNoteStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: LookupReleaseNoteStatusesUpdate }) =>
      client.update(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: lookupReleaseNoteStatusesKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: lookupReleaseNoteStatusesKeys.lists() });
    },
  });
};

/**
 * Hook to delete a lookup_release_note_statuse
 *
 * @returns React Query mutation for deleting lookup_release_note_statuse
 */
export const useDeleteLookupReleaseNoteStatuses = () => {
  const client = useLookupReleaseNoteStatusesClient();
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: (id: string) => client.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: lookupReleaseNoteStatusesKeys.all });
    },
  });
};

/**
 * Hook to count lookup_release_note_statuses with optional filtering
 *
 * ⚠️ FIELD RESTRICTIONS: Only filterable fields can be used: id, code
 *
 * @param filter - Optional filter using only filterable fields
 * @returns React Query result with count data
 */
export const useLookupReleaseNoteStatusesCount = (filter?: Partial<Record<typeof LookupReleaseNoteStatusesFieldConfig.filterable[number], any>>) => {
  const client = useLookupReleaseNoteStatusesClient();
  
  return useQuery({
    queryKey: lookupReleaseNoteStatusesKeys.count(filter),
    queryFn: () => client.count(filter),
  });
};

/**
 * Hook to get field configuration metadata
 *
 * @returns Field configuration with permission information
 */
export const useLookupReleaseNoteStatusesFieldConfig = () => {
  return LookupReleaseNoteStatusesFieldConfig;
};